// Pieza de `afterReferral`: tras derivar, evalúa y aparta lo que siga aplicando (ANY_FINANCIER) o suelta.
import * as Sentry from '@sentry/react';
import type { QueryClient } from '@tanstack/react-query';
import type { AfterReferral } from '@/domains/sales/wizard/api/useReferralFlow';
import { evaluationKey } from '../api/useWizardPromotions';
import { wizardPromotionsClient } from '../api/wizardPromotions.client';
import { withCustomerPhone } from '../lib/customerPhone';
import { AFTER_REFERRAL_TIMEOUT_MS } from '../constants';
import { reapplyOrRelease, type PromotionsApi } from '../lib/applySelection';
import { storePort } from '../store/promotionSelectionStore';
import { notifyDroppedPromotions } from './droppedPromotionNotice';

export interface AfterReferralDeps {
    api?: PromotionsApi;
    queryClient?: QueryClient;
}

export class AfterReferralTimeoutError extends Error {
    constructor() {
        super(`Promociones tardó más de ${String(AFTER_REFERRAL_TIMEOUT_MS)} ms tras derivar`);
        this.name = 'AfterReferralTimeoutError';
    }
}

function timeout(): { promise: Promise<never>; clear: () => void } {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const promise = new Promise<never>((_, reject) => {
        timer = setTimeout(() => {
            reject(new AfterReferralTimeoutError());
        }, AFTER_REFERRAL_TIMEOUT_MS);
    });
    return {
        promise,
        clear: () => {
            clearTimeout(timer);
        },
    };
}

/** Al vencer el tope se lanza y el wizard sigue al cobro; la escritura termina en segundo plano. */
export function createPromotionsAfterReferral({
    api = wizardPromotionsClient,
    queryClient,
}: AfterReferralDeps = {}): AfterReferral {
    return async ({ sessionId, refreshSession }) => {
        const work = reapplyOrRelease(
            sessionId,
            storePort(sessionId),
            withCustomerPhone(api, sessionId, queryClient),
        )
            .then((applied) => {
                notifyDroppedPromotions(sessionId, applied, { withoutSureKeep: true });
                return applied;
            })
            .finally(() => {
                void queryClient?.invalidateQueries({ queryKey: evaluationKey(sessionId) });
            });
        void work.then(refreshSession, () => undefined);
        const limit = timeout();
        try {
            await Promise.race([work, limit.promise]);
        } catch (error) {
            if (error instanceof AfterReferralTimeoutError) {
                Sentry.captureMessage('Promociones tras derivar sigue en segundo plano', {
                    level: 'warning',
                });
                work.catch((late: unknown) => Sentry.captureException(late));
            }
            throw error;
        } finally {
            limit.clear();
        }
    };
}
